/**
 * A rectangle of cells, on and off the clipboard.
 *
 * Two representations, and both are needed for different reasons.
 *
 * **Tab-separated text** is what every other application understands. Copying
 * out of a grid and into a spreadsheet, an email or an editor has to produce
 * something readable, so the text is the *rendered* value of each cell -- what
 * somebody looking at the grid would have read.
 *
 * **The values themselves** are what a paste back into this application should
 * restore, and the rendering cannot be turned back into them: a date renders in
 * the field's format and its timezone, a link row renders as the primary field's
 * text, and reading either back is lossy at best. The clipboard API only
 * reliably carries text, so the structured half is kept in `localStorage`,
 * keyed by the text it was written with -- which is how a paste can tell whether
 * the clipboard still holds what this application put there, or something
 * copied from somewhere else since.
 */
export const CLIPBOARD_KEY = 'lithos.clipboard'

/** A rectangle of cells, by row and then by column. */
export type CellGrid<T> = T[][]

export interface ClipboardPayload {
  /** The rendered text, exactly as it was written to the clipboard. */
  text: string
  /** The values behind it, in the same shape. */
  values: CellGrid<unknown>
}

const needsQuoting = (value: string) => /["\t\n\r]/.test(value)

/**
 * Renders a rectangle as tab-separated text.
 *
 * A cell holding a tab or a newline is wrapped in quotes and its own quotes
 * doubled, which is the convention every spreadsheet reads. Without it a single
 * long-text cell containing a line break turns one row into two, silently and
 * only sometimes.
 */
export function toTsv(cells: CellGrid<string>): string {
  return cells
    .map((row) =>
      row
        .map((value) =>
          needsQuoting(value) ? `"${value.split('"').join('""')}"` : value,
        )
        .join('\t'),
    )
    .join('\n')
}

/**
 * Reads tab-separated text back into a rectangle.
 *
 * Written as a scan rather than a split, because a split cannot tell a
 * separator from a character inside a quoted field -- which is the whole reason
 * the quoting exists.
 *
 * Carriage returns are dropped rather than treated as separators: text copied
 * from a Windows application arrives with `\r\n` line endings, and reading the
 * `\r` as content puts an invisible character at the end of every value.
 */
export function parseTsv(text: string): CellGrid<string> {
  const rows: CellGrid<string> = []
  let row: string[] = []
  let value = ''
  let quoted = false

  const endValue = () => {
    row.push(value)
    value = ''
  }
  const endRow = () => {
    endValue()
    rows.push(row)
    row = []
  }

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i]

    if (quoted) {
      if (char === '"') {
        // A doubled quote is one quote; a single one ends the field.
        if (text[i + 1] === '"') {
          value += '"'
          i += 1
        } else {
          quoted = false
        }
      } else {
        value += char
      }
      continue
    }

    if (char === '"' && value === '') quoted = true
    else if (char === '\t') endValue()
    else if (char === '\n') endRow()
    else if (char !== '\r') value += char ?? ''
  }

  // The last row has no newline after it, and a trailing newline must not
  // produce an empty row: text ending in one is one row, not two.
  if (value !== '' || row.length > 0) endRow()

  return rows
}

/** Puts the structured half where a later paste can find it. */
export function rememberClipboard(payload: ClipboardPayload): void {
  try {
    localStorage.setItem(CLIPBOARD_KEY, JSON.stringify(payload))
  } catch {
    // Storage access throws outright in a few configurations. A paste then
    // falls back to reading the text, which is a worse paste rather than none.
  }
}

/**
 * The values behind a piece of clipboard text, if they are this application's.
 *
 * The text is compared rather than trusted, because the clipboard can have been
 * written by anything since -- and restoring the values of a copy somebody made
 * ten minutes ago, over a paste from a spreadsheet, would be the strangest
 * possible bug to be looking at.
 *
 * Line endings are normalised for the comparison only. Some applications rewrite
 * `\n` as `\r\n` on the way through the clipboard, and the values are still the
 * ones that text was written with.
 */
export function recallClipboard(text: string): CellGrid<unknown> | null {
  const normalise = (value: string) => value.split('\r\n').join('\n')

  try {
    const raw = localStorage.getItem(CLIPBOARD_KEY)
    if (raw === null) return null

    const payload = JSON.parse(raw) as Partial<ClipboardPayload>
    if (typeof payload.text !== 'string' || !Array.isArray(payload.values)) return null

    return normalise(payload.text) === normalise(text) ? payload.values : null
  } catch {
    return null
  }
}

/** Test seam: a tab that has never copied anything. */
export function __resetClipboardForTests(): void {
  try {
    localStorage.removeItem(CLIPBOARD_KEY)
  } catch {
    // As above.
  }
}
